// The JSP page. Owns its box, its state and its events (data-j*). The Field Access page mounts it
// after Start JSP reserves the number, and gets it back through onExit.
// Design: MOCKUP_JSP_APPROVED.html rev 8 (approved 2026-10-08). Events and the LDL animation are
// ported from the mockup's handler. Rules: jsp.ts. Screens: jsp-screens.ts.

import "./jsp.css";
import type { AssetWork } from "./asset-work-screens";
import { REENERGIZE_CHECKS, answersOf, draftOf, editTo, railOf, jspAssetOf, jspTaskOf, nextPhase, isDead, stateFromDraft, type JspAsset, type JspDraft, type JspState, type JspTask } from "./jsp";
import { LMA, LMB, LVERIFY, ewFormProblem, jspHtml, leadPath, noContentHtml, noEewpView, posterOverlay, probesFor, rigDots, rigSeq, type EewpView, type JspView, type Term } from "./jsp-screens";
import { newEewpAnswers, printHtmlOf, type EewpFrom, type EewpSummary, type JustifyKey, type PermitView, type StandbyTick } from "./eewp";
import { reviewHtml } from "./resources";

export interface JspMount {
  work: AssetWork;
  taskId: string;
  jspNumber: string;
  /** The saved draft when he resumes, or null for a new JSP. */
  draft: JspDraft | null;
  /** callServer with the badge and company already added. */
  call: <T>(name: string, data: Record<string, unknown>) => Promise<T>;
  /** Back to the QEW page, with a line for the top of it. */
  onExit: (note: string) => void;
  onActivity: () => void;
  /** An overlay (PPE poster, SOP) opened or closed, so the Back gesture closes it first. */
  onOverlay?: (open: boolean) => void;
  /**
   * Item 5: reopen a submitted JSP at the close pill (Close JSP #n from the QEW page or the Dashboard card).
   * autoAt is set when the app already closed it and only his answer is missing.
   */
  reopen?: { autoAt: string | null };
}

/** What the Field Access page drives from the phone's Back gesture. */
export interface JspHandle {
  /** Same as Back To The QEW Page: the draft saves, the JSP stays a draft. */
  back(): void;
  closeOverlay(): void;
}

function esc(value: unknown): string {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

type Overlay = { kind: "poster" } | { kind: "sop"; url: string | null; error: string } | null;

export function mountJsp(box: HTMLElement, m: JspMount): JspHandle {
  const task = m.work.tasks.find((x) => x.taskId === m.taskId);
  const t: JspTask = jspTaskOf(task ?? { taskId: m.taskId, name: m.taskId, tier: 3 });
  if (!task || !t.hasContent) {
    box.innerHTML = noContentHtml(task?.name ?? m.taskId);
    box.addEventListener("click", (ev) => {
      if ((ev.target as HTMLElement).closest("[data-j='exit']")) m.onExit("");
    });
    return { back: () => m.onExit(""), closeOverlay: () => undefined };
  }
  const a: JspAsset = jspAssetOf(m.work.asset, m.work.engine);
  const v: JspView = {
    a,
    t,
    s: stateFromDraft(t, m.draft),
    jspNumber: m.jspNumber,
    pills: m.work.pills,
    close: { open: false, work: null, leftAs: null, note: "", isolation: null, reEnergize: {}, busy: false, done: false, error: "", autoAt: null },
    busy: false,
    error: "",
    ew: { ...noEewpView(), ctx: m.work.eewp ?? null, todayShort: m.work.today.slice(5).replace("-", "/") },
    cancel: {
      ask: false,
      busy: false,
      error: "",
      taskName: t.name,
      startedTime: m.work.draft?.jspNumber === m.jspNumber ? m.work.draft.startedTime : "",
    },
  };
  const ew: EewpView = v.ew!;
  // Resume: a draft that asked for an EEWP reads its permit before the node shows.
  const askedHere = m.work.draft?.jspNumber === m.jspNumber && Boolean(m.work.draft?.eewpStatus);
  if (askedHere && !m.reopen) ew.checking = true;
  if (m.reopen) {
    // Every node shows as done and the close pill is open.
    const rail = railOf(t, v.s);
    v.s = { ...v.s, submitted: true, phase: rail[rail.length - 1] ?? v.s.phase };
    v.close.open = true;
    v.close.autoAt = m.reopen.autoAt;
  }
  let overlay: Overlay = null;
  /** Opens or closes an overlay and tells the page, so Back closes it first. */
  function setOverlay(o: Overlay): void {
    const was = overlay !== null;
    overlay = o;
    if (was !== (o !== null)) m.onOverlay?.(o !== null);
  }
  let sopUrl: string | null = null;
  // Probe positions while the LDL rig animates.
  const lp: { a: { x: number; y: number }; b: { x: number; y: number }; anim: boolean } = { a: { x: 0, y: 0 }, b: { x: 0, y: 0 }, anim: false };

  const $ = (id: string) => box.querySelector<HTMLElement>(`#${id}`);

  function overlayHtml(): string {
    if (!overlay) return "";
    if (overlay.kind === "poster") return posterOverlay();
    const head = `${esc(t.sopNumber ?? "")} · ${esc(t.name)}`;
    const body = overlay.error
      ? `<div class="stopm">${esc(overlay.error)}</div>`
      : overlay.url
        ? `<iframe class="sopf" title="${esc(t.sopNumber ?? "SOP")}" src="${esc(overlay.url)}"></iframe>`
        : '<div class="txt">Opening the SOP…</div>';
    return `<div class="jsp"><div class="ov" role="dialog" aria-label="SOP"><div class="ovc wide"><div class="sh">${head}</div><div class="sb"><div class="txt">Reference only. It does not qualify or change anything.</div>${body}<button type="button" class="x" data-j="sopx">Back To The JSP</button></div></div></div></div>`;
  }

  function render(): void {
    box.innerHTML = jspHtml(v) + overlayHtml();
    document.body.style.overflow = overlay ? "hidden" : "";
    const sq = rigSeq(a);
    if (v.s.phase === "Test Dead" && v.s.ldl < sq.length) {
      const p = probesFor(a, sq[v.s.ldl]!);
      lp.a = { x: p.a.x, y: p.a.y };
      lp.b = { x: p.b.x, y: p.b.y };
      lp.anim = false;
    }
  }

  /** Scrolls so the element sits just under the sticky header. */
  function scrollUnderHeader(el: Element | null): void {
    if (!el) return;
    const hd = box.querySelector<HTMLElement>(".jh");
    window.scrollBy(0, el.getBoundingClientRect().top - (hd ? hd.offsetHeight : 0) - 8);
  }

  // ---- LDL rehearsal, ported from the mockup (live node-jsp.js S128b) ----

  const ease = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
  function lTerm(w: "A" | "B", p: Term): void {
    const f = $(`njLdlTerm${w}Fill`);
    const r = $(`njLdlTerm${w}Ring`);
    const l = $(`njLdlTerm${w}Label`);
    const s = $(`njLdlTerm${w}Sub`);
    for (const [el, cls] of [
      [f, "nj-ldl-term-fill"],
      [r, "nj-ldl-term-ring"],
    ] as const) {
      if (!el) continue;
      el.setAttribute("cx", String(p.x));
      el.setAttribute("cy", String(p.y));
      el.setAttribute("class", `${cls} ${p.kind}`);
    }
    if (l) {
      l.setAttribute("x", String(p.x));
      l.setAttribute("y", String(p.y + 4));
      l.textContent = p.label;
      l.setAttribute("class", `nj-ldl-term-label ${p.kind}`);
    }
    if (s) {
      s.setAttribute("x", String(p.x));
      s.setAttribute("y", String(p.y + 40));
      s.textContent = p.sub;
    }
  }
  function lDraw(): void {
    $("njLdlProbeA")?.setAttribute("transform", `translate(${lp.a.x},${lp.a.y})`);
    $("njLdlProbeB")?.setAttribute("transform", `translate(${lp.b.x},${lp.b.y})`);
    const pa = leadPath(LMA, lp.a);
    const pb = leadPath(LMB, lp.b);
    for (const id of ["njLdlLeadA", "njLdlHaloA"]) $(id)?.setAttribute("d", pa);
    for (const id of ["njLdlLeadB", "njLdlHaloB"]) $(id)?.setAttribute("d", pb);
  }
  function lAnim(ta: { x: number; y: number }, tb: { x: number; y: number }, fixedA: boolean): void {
    const btn = $("njLdlTraceBtn");
    btn?.classList.add("disabled");
    lp.anim = true;
    const sa = { ...lp.a };
    const sb = { ...lp.b };
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const t0 = performance.now();
    const dur = reduce ? 1 : 600;
    const tick = (now: number) => {
      const k = Math.min((now - t0) / dur, 1);
      const e = ease(k);
      lp.a = fixedA ? { ...ta } : { x: sa.x + (ta.x - sa.x) * e, y: sa.y + (ta.y - sa.y) * e };
      lp.b = { x: sb.x + (tb.x - sb.x) * e, y: sb.y + (tb.y - sb.y) * e };
      lDraw();
      if (k < 1) requestAnimationFrame(tick);
      else {
        lp.anim = false;
        btn?.classList.remove("disabled");
      }
    };
    requestAnimationFrame(tick);
  }
  /** One tap on the rig. True when the page should redraw (the last step). */
  function ldlAdv(): boolean {
    if (lp.anim) return false;
    const sq = rigSeq(a);
    const n = sq.length;
    if (v.s.ldl >= n - 1) {
      v.s.ldl = n;
      return true;
    }
    const pe = $("njLdlPairName");
    pe?.classList.add("fade");
    v.s.ldl++;
    window.setTimeout(() => {
      const step = sq[v.s.ldl]!;
      const isV = step.type === "verify";
      const ph = $("njLdlPhase");
      if (ph) {
        ph.textContent = step.phase;
        ph.className = `nj-ldl-phase ${isV ? "verify" : "walk"}`;
      }
      const sn = $("njLdlStepNum");
      if (sn) sn.textContent = `Step ${v.s.ldl + 1} of ${n}`;
      if (pe) {
        pe.textContent = step.label;
        pe.className = `nj-ldl-pairname ${isV ? "verify" : "walk"}`;
      }
      const mr = $("njLdlMeterReading");
      if (mr) {
        mr.textContent = isV ? "220" : "000";
        mr.setAttribute("class", isV ? "meter-reading verify" : "meter-reading");
      }
      const btn = $("njLdlTraceBtn");
      if (btn) {
        btn.className = `nj-ldl-trace-btn${isV ? " verify" : ""}`;
        btn.textContent = isV ? "Confirm Meter Re-check" : "Trace This Path";
      }
      $("njLdlSourceGroup")?.classList.toggle("hidden", !isV);
      $("njLdlTermA")?.classList.toggle("hidden", isV);
      $("njLdlTermB")?.classList.toggle("hidden", isV);
      const dt = $("njLdlDots");
      if (dt) dt.innerHTML = rigDots(a, v.s.ldl);
      if (isV) lAnim(LVERIFY.a, LVERIFY.b, false);
      else {
        const p = probesFor(a, step);
        lTerm("A", p.a);
        lTerm("B", p.b);
        lAnim(p.a, p.b, step.b === "G");
      }
    }, 180);
    return false;
  }

  // ---- Server steps ----

  async function submit(): Promise<void> {
    v.busy = true;
    v.error = "";
    render();
    try {
      await m.call("submitJsp", { jspNumber: m.jspNumber, answers: answersOf(v.s) });
      v.busy = false;
      v.s = { ...v.s, submitted: true };
      render();
      scrollUnderHeader(box.querySelector(".cbanner"));
    } catch (e) {
      v.busy = false;
      v.error = `The JSP was not submitted. ${messageOf(e)}`;
      render();
    }
  }

  async function closeFinal(): Promise<void> {
    const c = v.close;
    c.busy = true;
    c.error = "";
    render();
    try {
      await m.call("closeJsp", { jspNumber: m.jspNumber, close: {
          work: c.work,
          leftAs: c.leftAs,
          note: c.note,
          isolation: isDead(t, v.s) ? c.isolation : null,
          reEnergizeChecks: REENERGIZE_CHECKS.map(([k]) => k).filter((k) => c.reEnergize[k]),
        } });
      c.busy = false;
      c.done = true;
      c.open = false;
    } catch (e) {
      c.busy = false;
      c.error = c.autoAt ? `Your answer was not sent. ${messageOf(e)}` : `The JSP was not closed. ${messageOf(e)}`;
    }
    render();
  }

  async function closeWithoutIsolation(): Promise<void> {
    v.busy = true;
    render();
    try {
      await m.call("closeJsp", { jspNumber: m.jspNumber, withoutIsolation: true });
      exit(`JSP #${m.jspNumber.replace("JSP-", "")} closed without isolation.`);
    } catch (e) {
      v.busy = false;
      v.s = { ...v.s, closing: false };
      v.error = `The JSP was not closed. ${messageOf(e)}`;
      render();
    }
  }

  /** The SOP over the JSP. Review copy, logged as opened from this JSP. */
  async function openSop(): Promise<void> {
    const sop = m.work.sops.find((x) => x.sopNumber === t.sopNumber);
    setOverlay({ kind: "sop", url: null, error: "" });
    render();
    if (!sop) {
      overlay = { kind: "sop", url: null, error: `${t.sopNumber ?? "The SOP"} is not in the SOP Library.` };
      return render();
    }
    void m.call("logSopOpen", { sopNumber: sop.sopNumber, from: "JSP", jspNumber: m.jspNumber }).catch(() => undefined);
    try {
      const r = await fetch(sop.fileUrl);
      if (!r.ok) throw new Error(`${sop.sopNumber} did not load.`);
      const html = await r.text();
      if (overlay?.kind !== "sop") return;
      releaseSop();
      sopUrl = URL.createObjectURL(new Blob([reviewHtml(html)], { type: "text/html" }));
      overlay = { kind: "sop", url: sopUrl, error: "" };
    } catch (e) {
      if (overlay?.kind !== "sop") return;
      overlay = { kind: "sop", url: null, error: `Could not open the SOP. ${messageOf(e)}` };
    }
    render();
  }
  function releaseSop(): void {
    if (sopUrl) URL.revokeObjectURL(sopUrl);
    sopUrl = null;
  }

  // ---- EEWP, QEW side (EEWP_WORDS.md rev 2, MOCKUP_EEWP.html rev 2b) ----

  interface EewpLoad {
    permit: EewpSummary | null;
    print: PermitView | null;
    qews: { badgeId: string; name: string }[];
    location: string;
  }

  /** The permit changed: an approved one with PPE confirmed opens the JSP under it. */
  function takePermit(p: EewpSummary | null): void {
    ew.permit = p;
    const on = p?.status === "Approved" && Boolean(p.ppeConfirmedAt);
    if (on !== v.s.eewpApproved) v.s = { ...v.s, eewpApproved: on, eewpFrom: on ? (p?.from ?? null) : null };
    // Approved at Lock Out: the Tier 3 job is now energized, so Lock Out leaves the rail.
    const rail = railOf(t, v.s);
    if (!rail.includes(v.s.phase)) v.s = { ...v.s, phase: rail[rail.indexOf("Risk") + 1] ?? "Sign Off" };
  }

  // Rev 3 decision 7: the Waiting card refreshes by itself while the JSP is open.
  let poll: number | null = null;
  function watchPermit(): void {
    const waiting = ew.permit?.status === "Waiting" && !v.s.submitted;
    if (waiting && poll === null) poll = window.setInterval(() => void refreshPermit(), 15000);
    if (!waiting && poll !== null) {
      window.clearInterval(poll);
      poll = null;
    }
  }
  async function refreshPermit(): Promise<void> {
    if (ew.busy || !box.isConnected) {
      if (!box.isConnected && poll !== null) window.clearInterval(poll);
      return;
    }
    try {
      const r = await m.call<EewpLoad>("loadEewp", { jspNumber: m.jspNumber });
      const before = JSON.stringify(ew.permit);
      ew.print = r.print;
      if (JSON.stringify(r.permit) === before) return;
      takePermit(r.permit);
      keepScroll(render);
    } catch {
      // A missed refresh is fine. The next one tries again.
    }
    watchPermit();
  }

  /** Confirm PPE And Begin. Then the JSP opens under the permit. */
  async function confirmPpe(): Promise<void> {
    const p = ew.permit;
    if (!p) return;
    ew.busy = true;
    ew.error = "";
    keepScroll(render);
    try {
      const done = await m.call<EewpSummary>("confirmEewpPpe", { jspNumber: m.jspNumber, items: p.ppeItems.filter((_, i) => ew.ppeTicks[String(i)]) });
      ew.busy = false;
      takePermit(done);
      void saveDraft();
      render();
      scrollUnderHeader(box.querySelector(".rail .card"));
    } catch (e) {
      ew.busy = false;
      ew.error = `PPE was not confirmed. ${messageOf(e)}`;
      keepScroll(render);
    }
  }

  /** Print: the permit laid out like form 28.5 for the site safety file. */
  function printPermit(): void {
    if (!ew.print) return;
    const w = window.open("", "_blank");
    if (!w) {
      ew.error = "Your browser blocked the print window. Allow pop-ups for SafeTag and try again.";
      return;
    }
    w.document.open();
    w.document.write(printHtmlOf(ew.print));
    w.document.close();
  }

  /** The permit for this JSP, the site's other Active QEWs and the asset's location. */
  async function loadEewp(): Promise<void> {
    try {
      const r = await m.call<EewpLoad>("loadEewp", { jspNumber: m.jspNumber });
      takePermit(r.permit);
      ew.print = r.print;
      ew.qews = r.qews;
      ew.qewsLoaded = true;
      ew.location = r.location;
      ew.error = "";
    } catch (e) {
      ew.error = `The EEWP did not load. ${messageOf(e)}`;
      ew.qewsLoaded = true;
    }
    ew.checking = false;
    keepScroll(render);
    watchPermit();
  }

  function startEewp(from: EewpFrom): void {
    ew.flow = { step: "ask1", from, answers: newEewpAnswers(), photo: null };
    ew.error = "";
    if (!ew.qewsLoaded) void loadEewp();
  }

  /** Go De-Energized: the gold path. Need Power? turns to de-energize; the other links go back to their node. */
  function goDeEnergized(): void {
    const from = ew.flow?.from;
    ew.flow = null;
    ew.error = "";
    if (from === "needPower") v.s = { ...v.s, plan: "dead", planWhy: null };
    else if (from === "over40" && v.s.whyRead) v.s = nextPhase(t, { ...v.s, deen: true });
  }

  /** Yes, Request An Outage: a work request for the outage, and this JSP stops. */
  async function requestOutage(): Promise<void> {
    ew.busy = true;
    ew.error = "";
    keepScroll(render);
    try {
      const r = await m.call<{ requestId: string }>("requestOutage", { jspNumber: m.jspNumber });
      exit(`JSP #${m.jspNumber.replace("JSP-", "")} stopped. Work request ${r.requestId} asks for a planned outage.`);
    } catch (e) {
      ew.busy = false;
      ew.error = `The work request was not written. ${messageOf(e)}`;
      keepScroll(render);
    }
  }

  /** Submit For Approval: the permit is written and the JSP waits. No work starts. */
  async function submitEewp(): Promise<void> {
    const f = ew.flow;
    if (!f) return;
    ew.busy = true;
    ew.error = "";
    keepScroll(render);
    try {
      const permit = await m.call<EewpSummary>("requestEewp", { jspNumber: m.jspNumber, from: f.from, eewp: f.answers, draft: draftOf(v.s), photo: f.photo });
      ew.busy = false;
      ew.flow = null;
      takePermit(permit);
      render();
      scrollUnderHeader(box.querySelector(".ewwait"));
      watchPermit();
      void refreshPermit();
    } catch (e) {
      ew.busy = false;
      ew.error = `The EEWP was not sent. ${messageOf(e)}`;
      keepScroll(render);
    }
  }

  /** Withdraw returns to the JSP, where the QEW can go de-energized or discard. */
  async function withdrawEewp(): Promise<void> {
    ew.busy = true;
    ew.error = "";
    keepScroll(render);
    try {
      takePermit(await m.call<EewpSummary>("withdrawEewp", { jspNumber: m.jspNumber }));
      watchPermit();
      ew.busy = false;
      render();
      scrollUnderHeader(box.querySelector(".rail .card"));
    } catch (e) {
      ew.busy = false;
      ew.error = `The EEWP was not withdrawn. ${messageOf(e)}`;
      keepScroll(render);
    }
  }

  /** The photo, shrunk to a small JPEG on the phone before it is sent. */
  async function takePhoto(file: File): Promise<void> {
    const f = ew.flow;
    if (!f) return;
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      await new Promise<void>((ok, bad) => {
        img.onload = () => ok();
        img.onerror = () => bad(new Error("That file is not a photo."));
        img.src = url;
      });
      const k = Math.min(1, 1280 / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement("canvas");
      c.width = Math.round(img.naturalWidth * k);
      c.height = Math.round(img.naturalHeight * k);
      c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      f.photo = c.toDataURL("image/jpeg", 0.72);
      ew.error = "";
    } catch (e) {
      ew.error = `The photo did not load. ${messageOf(e)}`;
    }
    keepScroll(render);
  }

  /** One step back inside the EEWP screens. False when none is open. */
  function eewpBack(): boolean {
    const f = ew.flow;
    if (!f || ew.busy) return false;
    ew.error = "";
    if (f.step === "review") f.step = "form";
    else if (f.step === "form") f.step = "ask2";
    else if (f.step === "ask2") f.step = "ask1";
    else ew.flow = null;
    render();
    scrollUnderHeader(box.querySelector(".rail .card"));
    return true;
  }

  function keepScroll(fn: () => void): void {
    const keep = window.scrollY;
    fn();
    window.scrollTo(0, keep);
  }

  /** Saves the draft for Back and Resume. Never blocks the JSP. True when saved. */
  let saving: Promise<boolean> = Promise.resolve(true);
  function saveDraft(): Promise<boolean> {
    if (v.s.submitted) return Promise.resolve(true);
    const draft = draftOf(v.s);
    saving = saving.then(() =>
      m
        .call("saveJspDraft", { jspNumber: m.jspNumber, draft })
        .then(() => true)
        .catch(() => false),
    );
    return saving;
  }

  /** Back To The QEW Page before Submit. The JSP stays a draft he can resume. */
  async function backToQew(): Promise<void> {
    const ok = await saveDraft();
    exit(ok ? "" : `JSP #${m.jspNumber.replace("JSP-", "")} is still a draft, but your last answers did not save. Resume picks up at the last finished node.`);
  }

  /** Cancel JSP, confirmed. Before Submit only. The record keeps it (the discard path), then back to the QEW page. */
  async function cancelJsp(): Promise<void> {
    const c = v.cancel!;
    if (v.s.submitted || c.busy) return;
    c.busy = true;
    c.error = "";
    render();
    await saving;
    try {
      await m.call("discardJsp", { jspNumber: m.jspNumber });
    } catch (e) {
      c.busy = false;
      c.error = `JSP #${m.jspNumber.replace("JSP-", "")} was not cancelled. ${messageOf(e)}`;
      render();
      return;
    }
    exit(`JSP #${m.jspNumber.replace("JSP-", "")} cancelled.`);
  }

  function exit(note: string): void {
    if (poll !== null) window.clearInterval(poll);
    poll = null;
    releaseSop();
    document.body.style.overflow = "";
    m.onExit(note);
  }

  // ---- Events ----

  box.addEventListener("click", (ev) => {
    const b = (ev.target as HTMLElement).closest<HTMLButtonElement>("button");
    if (!b || b.disabled || !box.contains(b)) return;
    m.onActivity();
    const d = b.dataset;
    const s = v.s;
    const fromPhase = s.phase;
    let scroll: "card" | "keep" = "keep";
    if (d.j === "ldladv" && !ldlAdv()) return;
    if (d.jgo) {
      v.s = editTo(t, s, d.jgo);
      scroll = "card";
    } else if (d.jplan) {
      s.plan = d.jplan as JspState["plan"];
      s.planWhy = null;
    } else if (d.jpw) s.planWhy = d.jpw;
    else if (d.jfit) s.fit = d.jfit as JspState["fit"];
    else if (d.jcd) {
      const f = d.jcd as keyof JspState["cond"];
      (s.cond as Record<string, unknown>)[f] = d.v === "1";
      if (f === "sense" && d.v === "0") s.cond.senses = [];
    } else if (d.jsn) {
      const i = s.cond.senses.indexOf(d.jsn);
      if (i > -1) s.cond.senses.splice(i, 1);
      else s.cond.senses.push(d.jsn);
    } else if (d.jtp) s.cond.tamper = d.jtp as JspState["cond"]["tamper"];
    else if (d.jhz) {
      s.hz = d.jhz as JspState["hz"];
      s.lv = null;
      s.rs = null;
    } else if (d.jlv) s.lv = d.jlv as JspState["lv"];
    else if (d.jrs) s.rs = d.jrs;
    else if (d.jpr) {
      const ans = d.v === "yes" ? "yes" : d.v === "dontKnow" ? "dontKnow" : "no";
      // A changed answer drops its acknowledgment. A De-Energize answer changed back removes the de-energized steps.
      if (s.prompts[d.jpr] !== ans) delete s.escalAck[d.jpr];
      s.prompts[d.jpr] = ans;
    } else if (d.jea) s.escalAck[d.jea] = true;
    else if (d.jdeen) s.escalAck[d.jdeen] = true;
    else if (d.jsrc) s.srcCount = Number(d.jsrc) as JspState["srcCount"];
    else if (d.jdoor) {
      s.door = d.jdoor as JspState["door"];
      if (d.jdoor === "closed") s.reason = null;
    } else if (d.jrsn) s.reason = d.jrsn;
    else if (d.jirc) s.irCover = d.jirc as JspState["irCover"];
    else if (d.jsb) s.standbyMode = d.jsb as JspState["standbyMode"];
    else if (d.jgr) s.grounding = d.jgr === "1";
    else if (d.jla) s.liveRemain = d.jla === "1";
    else if (d.js2) s.s2[d.js2] = !s.s2[d.js2];
    else if (d.jeg) {
      s.energy[d.jeg] = !s.energy[d.jeg];
      if (s.energy[d.jeg]) s.energyNone = false;
      else delete s.energyConfirm[d.jeg];
    } else if (d.jec) {
      if (s.energyConfirm[d.jec]) delete s.energyConfirm[d.jec];
      else s.energyConfirm[d.jec] = "confirmed";
    }
    else if (d.jew) {
      startEewp(d.jew as EewpFrom);
      scroll = "card";
    } else if (d.jewc && ew.flow) {
      const cats = ew.flow.answers.justificationCategories;
      const k = d.jewc as JustifyKey;
      ew.flow.answers.justificationCategories = cats.includes(k) ? cats.filter((x) => x !== k) : [...cats, k];
    } else if (d.jewpd && ew.flow) ew.flow.answers.partDead = d.jewpd === "yes" ? "yes" : "no";
    else if (d.jews && ew.flow) ew.flow.answers.safeToProceed = d.jews === "yes" ? "yes" : "no";
    else if (d.jewsb && ew.flow) {
      const k = d.jewsb as StandbyTick;
      const ticks = ew.flow.answers.standbyTicks;
      ew.flow.answers.standbyTicks = ticks.includes(k) ? ticks.filter((x) => x !== k) : [...ticks, k];
    } else if (d.jewppe !== undefined) ew.ppeTicks[d.jewppe] = !ew.ppeTicks[d.jewppe];
    else if (d.jcf) s.c[d.jcf] = !s.c[d.jcf];
    else if (d.jgl) {
      s.gloves = d.jgl === "fail" ? "fail" : "pass";
      s.c.ppeGloves = true;
    } else if (d.jcw) v.close.work = d.jcw;
    else if (d.jleft) v.close.leftAs = d.jleft;
    else if (d.jrts) {
      v.close.isolation = d.jrts;
      if (d.jrts !== "rts") v.close.reEnergize = {};
    } else if (d.jre) v.close.reEnergize[d.jre] = !v.close.reEnergize[d.jre];
    else
      switch (d.j) {
        case "next":
          v.s = nextPhase(t, s);
          scroll = "card";
          break;
        case "ackdeen":
          v.s = nextPhase(t, { ...s, deen: true });
          scroll = "card";
          break;
        case "why":
          s.whyOpen = !s.whyOpen;
          s.whyRead = true;
          break;
        case "raise":
          s.raising = true;
          break;
        case "raisecancel":
          s.raising = false;
          s.hz = s.lv = null;
          s.rs = null;
          break;
        case "raiseapply":
          if (s.hz && s.lv && s.rs) s.raised = { hz: s.hz, lv: s.lv, rs: s.rs };
          s.raising = false;
          s.hz = s.lv = null;
          s.rs = null;
          break;
        case "ldladv":
          break;
        case "enone":
          s.energyNone = !s.energyNone;
          if (s.energyNone) {
            s.energy = {};
            s.energyConfirm = {};
          }
          break;
        case "close":
          s.closing = true;
          break;
        case "back":
          s.closing = false;
          break;
        case "closenoiso":
          void closeWithoutIsolation();
          return;
        case "submit":
          void submit();
          return;
        case "openclose":
          v.close.open = true;
          break;
        case "closefinal":
          void closeFinal();
          return;
        case "poster":
          setOverlay({ kind: "poster" });
          break;
        case "postok":
          s.c.poster = true;
          setOverlay(null);
          break;
        case "ovx":
        case "sopx":
          setOverlay(null);
          releaseSop();
          break;
        case "sop":
          void openSop();
          return;
        case "ewdead":
          goDeEnergized();
          scroll = "card";
          break;
        case "ewback":
          eewpBack();
          return;
        case "ewask2":
          if (ew.flow) ew.flow.step = "ask2";
          scroll = "card";
          break;
        case "ewform":
          if (ew.flow) ew.flow.step = "form";
          ew.error = "";
          scroll = "card";
          break;
        case "ewreview":
          if (ew.flow && !ewFormProblem(v)) ew.flow.step = "review";
          scroll = "card";
          break;
        case "ewoutage":
          void requestOutage();
          return;
        case "ewsubmit":
          void submitEewp();
          return;
        case "ewwithdraw":
          void withdrawEewp();
          return;
        case "ewppe":
          void confirmPpe();
          return;
        case "ewprint":
          printPermit();
          break;
        case "exit":
          exit(v.close.done ? `JSP #${m.jspNumber.replace("JSP-", "")} closed.` : "");
          return;
        case "backqew":
          void backToQew();
          return;
        case "cancelask":
          v.cancel!.ask = true;
          v.cancel!.error = "";
          render();
          window.scrollTo(0, 0);
          return;
        case "cancelkeep":
          v.cancel!.ask = false;
          v.cancel!.error = "";
          render();
          return;
        case "cancelgo":
          void cancelJsp();
          return;
        default:
          return;
      }
    // De-Energize This Job from Do The Work: that node leaves the rail. Go to the first de-energized step.
    const rail = railOf(t, v.s);
    if (!rail.includes(v.s.phase)) {
      v.s = { ...v.s, phase: rail[rail.indexOf("Risk") + 1] ?? "Sign Off" };
      scroll = "card";
    }
    // A finished node saves the draft (QEW Page Return Path).
    if (v.s.phase !== fromPhase && !v.s.submitted) void saveDraft();
    const keep = window.scrollY;
    render();
    if (scroll === "card") scrollUnderHeader(box.querySelector(".rail .card"));
    else window.scrollTo(0, keep);
  });

  // Glove Fail: What Did You Find? The pick redraws the node so Other can open its line.
  box.addEventListener("change", (ev) => {
    const el = ev.target as HTMLSelectElement;
    if (el.id === "ewq5" && ew.flow) {
      ew.flow.answers.secondQewId = el.value;
      keepScroll(render);
      return;
    }
    if (el.id === "ewphoto") {
      const file = (el as unknown as HTMLInputElement).files?.[0];
      if (file) void takePhoto(file);
      return;
    }
    if (el.id !== "jgdef") return;
    v.s.gloveDefect = el.value || null;
    const keep = window.scrollY;
    render();
    window.scrollTo(0, keep);
  });

  // The job brief, the worse note and the glove Other line keep focus while he types.
  // The EEWP answers keep focus while the QEW types. The page redraws only when Continue turns on or off.
  const EW_FIELDS: Record<string, "liveWork" | "justification" | "ifDeEnergized" | "partDeadWhat" | "emergencyPlan" | "standbyName" | "standbyTitle"> = {
    ewsbn: "standbyName",
    ewsbt: "standbyTitle",
    ewq1: "liveWork",
    ewq2: "justification",
    ewq3: "ifDeEnergized",
    ewq4: "partDeadWhat",
    ewq6: "emergencyPlan",
  };
  box.addEventListener("input", (ev) => {
    const el = ev.target as HTMLTextAreaElement;
    const field = EW_FIELDS[el.id];
    if (field && ew.flow) {
      const was = ewFormProblem(v) === null;
      ew.flow.answers[field] = el.value;
      if (was === (ewFormProblem(v) === null)) return;
      const pos = el.selectionStart;
      const id = el.id;
      keepScroll(render);
      const again = box.querySelector<HTMLTextAreaElement>(`#${id}`);
      if (again) {
        again.focus();
        again.setSelectionRange(pos, pos);
      }
      return;
    }
    if (el.id === "jworse") {
      v.close.note = el.value;
      return;
    }
    if (el.id === "jgnote") {
      const had = Boolean(v.s.gloveDefectNote.trim());
      v.s.gloveDefectNote = el.value;
      if (had === Boolean(v.s.gloveDefectNote.trim())) return;
      const pos = el.selectionStart;
      const keep = window.scrollY;
      render();
      window.scrollTo(0, keep);
      const nn = box.querySelector<HTMLInputElement>("#jgnote");
      if (nn) {
        nn.focus();
        nn.setSelectionRange(pos, pos);
      }
      return;
    }
    if (el.id !== "jbrief") return;
    const had = Boolean(v.s.brief.trim());
    v.s.brief = el.value;
    if (had === Boolean(v.s.brief.trim())) return;
    const pos = el.selectionStart;
    const keep = window.scrollY;
    render();
    window.scrollTo(0, keep);
    const nb = box.querySelector<HTMLTextAreaElement>("#jbrief");
    if (nb) {
      nb.focus();
      nb.setSelectionRange(pos, pos);
    }
  });

  render();
  window.scrollTo(0, 0);
  if (ew.checking) void loadEewp();
  return {
    back: () => {
      if (eewpBack()) return;
      if (v.s.submitted) exit(v.close.done ? `JSP #${m.jspNumber.replace("JSP-", "")} closed.` : "");
      else void backToQew();
    },
    closeOverlay: () => {
      setOverlay(null);
      releaseSop();
      render();
    },
  };
}
